import { isInFlightStatus, isFailedStatus, isActiveProcessingStatus } from "@/constants/documentStatus";
import { AlertType, DocumentRead, DocumentStatus, ResourceName } from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { useRef, useEffect } from "react";
import { useGetList, useNotify } from "react-admin";
import { resolvePollInterval } from "./useDocumentStatusPolling";

/**
 * The list's cadence follows the same ladder as the single-document poll: keyed
 * off how long the set of in-flight statuses has been unchanged, on the active
 * rungs when any document is PROCESSING / LOADING and the waiting rungs
 * otherwise. Any change to that set resets to the first rung.
 */
export const resolveListPollInterval = (documents: DocumentRead[] | undefined, elapsedMs: number): number | false => {
  const inFlight = (Array.isArray(documents) ? documents : []).filter((d) => isInFlightStatus(d.status));
  if (inFlight.length === 0) return false;
  const representative = inFlight.some((d) => isActiveProcessingStatus(d.status))
    ? DocumentStatus.PROCESSING
    : DocumentStatus.UPLOADED;
  return resolvePollInterval(representative, elapsedMs);
};

/** Stable fingerprint of which documents are in flight and in what status. */
const inFlightSignature = (documents: DocumentRead[] | undefined): string =>
  (documents ?? [])
    .filter((d) => isInFlightStatus(d.status))
    .map((d) => `${d.id}:${d.status}`)
    .sort()
    .join("|");

/**
 * Hook to poll the document list when any documents are in in-flight states.
 *
 * Polls the documents list while at least one document is in a non-terminal
 * state (uploaded, processing, reprocess, approved, loading, loaded), on the
 * same transition-reset ladder the document page uses (2/4/8 s while anything
 * is actively processing, 3/6/15 s while everything is queued). Stops when all
 * documents reach terminal states.
 *
 * Detects status transitions by comparing a ref-stored Map<docId, status>
 * with each poll response. Shows a toast notification for any document that
 * transitions to a failed state, so users notice failures in long lists.
 *
 * @param documents - Array of documents to check for in-flight status
 */
export const useDocumentListPolling = (documents?: DocumentRead[]) => {
  const notify = useNotify();
  const statusMapRef = useRef<Map<string, DocumentStatus>>(new Map());
  // The ladder's clock: when the in-flight set last changed.
  const signatureRef = useRef<string>(inFlightSignature(documents));
  const sinceRef = useRef<number>(Date.now());
  const signature = inFlightSignature(documents);
  if (signature !== signatureRef.current) {
    signatureRef.current = signature;
    sinceRef.current = Date.now();
  }

  // Poll if ANY document is in a non-terminal state
  const hasInFlightDocuments = documents?.some((doc) => isInFlightStatus(doc.status));

  const { data: polledDocuments } = useGetList<DocumentRead>(
    ResourceName.DOCUMENTS,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "uploaded_datetime", order: "DESC" },
      filter: {},
    },
    {
      // useGetList caches { data, total, pageInfo }, not the array: read .data.
      // (Reading the wrapper crashed the Documents list with "filter is not a
      // function" the first time the poll had cached data.)
      refetchInterval: (query) =>
        resolveListPollInterval(
          (query.state.data as { data?: DocumentRead[] } | undefined)?.data ?? documents,
          Date.now() - sinceRef.current
        ),
      refetchOnWindowFocus: true,
      enabled: hasInFlightDocuments,
    }
  );

  // Detect status transitions and notify on failures
  useEffect(() => {
    if (!polledDocuments) return;

    const prevMap = statusMapRef.current;
    const newMap = new Map<string, DocumentStatus>();

    for (const doc of polledDocuments) {
      if (!doc.id || !doc.status) continue;

      newMap.set(doc.id, doc.status);

      const prevStatus = prevMap.get(doc.id);

      // Skip if this is a new document (no previous status) or status unchanged
      if (prevStatus === undefined || prevStatus === doc.status) continue;

      // Notify if the document transitioned TO a failed state
      if (isFailedStatus(doc.status)) {
        const docName = doc.name || `Document ${doc.id}`;
        const statusLabel = formatToTitleCase(doc.status);
        notify(`${docName}: ${statusLabel}`, { type: AlertType.ERROR });
      }
    }

    statusMapRef.current = newMap;
  }, [polledDocuments, notify]);
};
